import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MarketTrade } from '../api/useMarketTrades';
import type { TradeOverlayData } from '../components/charts/convergence';
import { useTraderTradesCache } from '../api/useTraderTradesCache';
import { getAccountColor } from '../components/TraderInsights';
import { toChecksumId } from '../utils/ids';

function parseTraderAddresses(param: string | null): Set<string> {
    if (!param) return new Set();
    return new Set(
        param
            .split(',')
            .filter(Boolean)
            .map(a => toChecksumId(`${a}`))
    );
}

function serializeTraderAddresses(addresses: Set<string>): string {
    return [...addresses].map(id => id.replace(/^polymarket:/, '')).join(',');
}

interface UseTraderOverlayParams {
    tradeMarketId: string | null;
    liveTrades: Map<string, any[]>;
    eventParam: string | undefined;
    eventId: string | null;
    eventStartDate?: string | null;
    eventEndDate?: string | null;
}

export function useTraderOverlay({
    tradeMarketId,
    liveTrades,
    eventParam,
    eventId,
    eventStartDate,
    eventEndDate,
}: UseTraderOverlayParams) {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    const [highlightedAccounts, setHighlightedAccounts] = useState<Set<string>>(
        () => parseTraderAddresses(searchParams.get('traders'))
    );

    // Re-read ?traders= when navigating to a different event
    const prevEventParam = useRef(eventParam);
    useEffect(() => {
        if (eventParam === prevEventParam.current) return;
        prevEventParam.current = eventParam;
        const fromUrl = parseTraderAddresses(
            searchParamsRef.current.get('traders')
        );
        if (fromUrl.size > 0) setHighlightedAccounts(fromUrl);
    }, [eventParam]);

    const accountColorMap = useMemo(() => {
        const colors = new Map<string, string>();
        let idx = 0;
        for (const accountId of highlightedAccounts) {
            colors.set(accountId, getAccountColor(idx));
            idx++;
        }
        return colors;
    }, [highlightedAccounts]);

    const { tradesByAccount, loadingAccounts, loadTrader, clearCache } =
        useTraderTradesCache(
            tradeMarketId,
            eventId,
            eventStartDate,
            eventEndDate
        );

    // Load URL-provided traders once per tradeMarketId
    const urlTradersLoaded = useRef(false);
    const highlightedAccountsRef = useRef(highlightedAccounts);
    useEffect(() => {
        highlightedAccountsRef.current = highlightedAccounts;
    }, [highlightedAccounts]);

    useEffect(() => {
        if (!tradeMarketId || urlTradersLoaded.current) return;
        urlTradersLoaded.current = true;
        for (const address of highlightedAccountsRef.current)
            loadTrader(address);
    }, [tradeMarketId, loadTrader]);

    useEffect(() => {
        urlTradersLoaded.current = false;
        clearCache();
    }, [tradeMarketId, clearCache]);

    const updateHighlightedAccounts = useCallback(
        (next: Set<string>) => {
            setHighlightedAccounts(next);
            const params = new URLSearchParams(searchParamsRef.current);
            if (next.size > 0) {
                params.set('traders', serializeTraderAddresses(next));
            } else {
                params.delete('traders');
            }
            setSearchParams(params, { replace: true });
        },
        [setSearchParams]
    );

    const handleToggleAccount = useCallback(
        (accountId: string) => {
            const normalizedId = toChecksumId(accountId);
            setHighlightedAccounts(prev => {
                const next = new Set(prev);
                if (next.has(normalizedId)) {
                    next.delete(normalizedId);
                } else {
                    next.add(normalizedId);
                    loadTrader(normalizedId);
                }
                updateHighlightedAccounts(next);
                return next;
            });
        },
        [loadTrader, updateHighlightedAccounts]
    );

    // Merge live RTDB trades into tradesByAccount for highlighted traders
    const mergedTradesByAccount = useMemo(() => {
        if (
            !tradeMarketId ||
            highlightedAccounts.size === 0 ||
            liveTrades.size === 0
        ) {
            return tradesByAccount;
        }
        const rtdbTrades = liveTrades.get(tradeMarketId);
        if (!rtdbTrades || rtdbTrades.length === 0) return tradesByAccount;

        const liveByAccount = new Map<string, MarketTrade[]>();
        for (const t of rtdbTrades) {
            const addr = toChecksumId(`${t.account}`);
            if (!highlightedAccounts.has(addr)) continue;
            let list = liveByAccount.get(addr);
            if (!list) {
                list = [];
                liveByAccount.set(addr, list);
            }
            list.push({
                id: t.transaction_id ?? '',
                marketId: t.market_id ?? tradeMarketId,
                timestamp: new Date(t.timestamp),
                price: t.price,
                side: t.side.toLowerCase() as 'buy' | 'sell',
                isMaker: t.is_maker ?? false,
                outcomeIndex: t.outcome_index ?? 0,
                amount: t.base_amount,
                quoteAmount: t.quote_amount,
                account: t.account ?? '',
                source: 'live' as const,
                txHash: null,
            });
        }

        if (liveByAccount.size === 0) return tradesByAccount;

        const merged = new Map(tradesByAccount);
        for (const [account, liveTList] of liveByAccount) {
            const existing = merged.get(account) ?? [];
            const existingKeys = new Set(
                existing.map(
                    t =>
                        t.id ||
                        `${t.timestamp.getTime()}_${t.price}_${t.amount}`
                )
            );
            const newTrades = liveTList.filter(t => {
                const key =
                    t.id || `${t.timestamp.getTime()}_${t.price}_${t.amount}`;
                return !existingKeys.has(key);
            });
            if (newTrades.length > 0)
                merged.set(account, [...existing, ...newTrades]);
        }
        return merged;
    }, [tradeMarketId, highlightedAccounts, tradesByAccount, liveTrades]);

    const tradeOverlay = useMemo((): TradeOverlayData | undefined => {
        if (!tradeMarketId || highlightedAccounts.size === 0) return undefined;
        const allTrades: MarketTrade[] = [];
        for (const accountId of highlightedAccounts) {
            const trades = mergedTradesByAccount.get(accountId);
            if (trades) allTrades.push(...trades);
        }
        if (allTrades.length === 0) return undefined;
        return { trades: allTrades, highlightedAccounts, accountColorMap };
    }, [
        tradeMarketId,
        highlightedAccounts,
        mergedTradesByAccount,
        accountColorMap,
    ]);

    return {
        highlightedAccounts,
        accountColorMap,
        loadingAccounts,
        tradesByAccount,
        mergedTradesByAccount,
        tradeOverlay,
        handleToggleAccount,
    };
}
